import { useState } from "react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, arrayMove, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  createDegree, updateDegree, deleteDegree, reorderDegrees,
  updateChapter, deleteChapter, createChapter, reorderChapters,
} from "@/api/exercises";
import type { Degree, Chapter } from "@/types";

interface Props {
  courseId: number;
  degrees: Degree[];
  chapters: Chapter[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}

/** Gestion de la taxonomie (degrés + chapitres). Source de vérité fiable : renommer
 *  un degré se répercute partout ; un degré/chapitre utilisé ne se supprime pas par
 *  accident (refus serveur). Réordonnancement des degrés au glisser-déposer. */
export default function TaxonomyManager({ courseId, degrees, chapters, onClose, onChanged }: Props) {
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [newCode, setNewCode]   = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newChapter, setNewChapter] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const guard = async (p: Promise<unknown>) => {
    setError(null);
    try { await p; await onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : "Erreur."); }
  };

  const addDegree = () => {
    const code = newCode.trim();
    if (!code) return;
    guard(createDegree(courseId, code, newLabel.trim() || code)).then(() => { setNewCode(""); setNewLabel(""); });
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = degrees.findIndex((d) => d.id === active.id);
    const newIdx = degrees.findIndex((d) => d.id === over.id);
    guard(reorderDegrees(courseId, arrayMove(degrees, oldIdx, newIdx).map((d) => d.id)));
  };

  const onDragEndChapter = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = chapters.findIndex((c) => c.id === active.id);
    const newIdx = chapters.findIndex((c) => c.id === over.id);
    guard(reorderChapters(courseId, arrayMove(chapters, oldIdx, newIdx).map((c) => c.id)));
  };

  const removeDegree = async (d: Degree) => {
    const ok = await confirm({
      title: `Supprimer le degré « ${d.label} » ?`,
      message: "Possible uniquement s'il n'est utilisé par aucun exercice.",
      confirmLabel: "Supprimer", danger: true,
    });
    if (ok) guard(deleteDegree(d.id));
  };

  const removeChapter = async (c: Chapter) => {
    const ok = await confirm({
      title: `Supprimer le chapitre « ${c.title} » ?`,
      message: "Les exercices liés ne seront plus rattachés à ce chapitre.",
      confirmLabel: "Supprimer", danger: true,
    });
    if (ok) guard(deleteChapter(c.id));
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Degrés et chapitres">
        <div style={styles.head}>
          <h2 style={styles.title}>Degrés &amp; chapitres</h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Fermer" title="Fermer"><Icon name="clear" size={18} /></button>
        </div>

        {error && <div style={styles.error}><Icon name="alert" size={15} style={{ color: "var(--red)", flexShrink: 0 }} /> {error}</div>}

        <div style={styles.body}>
          {/* Degrés */}
          <section style={styles.section}>
            <h3 style={styles.h3}>Degrés (années)</h3>
            <p style={styles.note}>Le <strong>code</strong> est l'identifiant stable ; le <strong>libellé</strong> est ce que voit l'élève. Glisse pour réordonner.</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={degrees.map((d) => d.id)} strategy={verticalListSortingStrategy}>
                {degrees.map((d) => (
                  <DegreeRow
                    key={d.id}
                    degree={d}
                    onSave={(data) => guard(updateDegree(d.id, data))}
                    onDelete={() => removeDegree(d)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <div style={styles.addRow}>
              <input style={{ ...styles.input, width: 90 }} placeholder="code (4in)" value={newCode} onChange={(e) => setNewCode(e.target.value)} />
              <input style={{ ...styles.input, flex: 1 }} placeholder="libellé (4ème année)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") addDegree(); }} />
              <Button variant="secondary" size="sm" onClick={addDegree}>Ajouter</Button>
            </div>
          </section>

          {/* Chapitres */}
          <section style={styles.section}>
            <h3 style={styles.h3}>Chapitres</h3>
            <p style={styles.note}>Glisse pour réordonner. L'œil affiche / masque un chapitre aux élèves.</p>
            {chapters.length === 0 && <p style={styles.note}>Aucun chapitre. Crée-en un ci-dessous ou depuis un exercice.</p>}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndChapter}>
              <SortableContext items={chapters.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {chapters.map((c) => (
                  <ChapterRow
                    key={c.id}
                    chapter={c}
                    onSave={(title) => guard(updateChapter(c.id, { title }))}
                    onToggleVisible={() => guard(updateChapter(c.id, { visible: c.visible === false }))}
                    onDelete={() => removeChapter(c)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <div style={styles.addRow}>
              <input style={{ ...styles.input, flex: 1 }} placeholder="Nouveau chapitre…" value={newChapter} onChange={(e) => setNewChapter(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter" && newChapter.trim()) guard(createChapter(courseId, newChapter.trim())).then(() => setNewChapter("")); }} />
              <Button variant="secondary" size="sm" onClick={() => { if (newChapter.trim()) guard(createChapter(courseId, newChapter.trim())).then(() => setNewChapter("")); }}>Ajouter</Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function DegreeRow({ degree, onSave, onDelete }: {
  degree: Degree;
  onSave: (data: { code?: string; label?: string }) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: degree.id });
  const [code, setCode]   = useState(degree.code);
  const [label, setLabel] = useState(degree.label);

  const commitCode  = () => { const v = code.trim(); if (v && v !== degree.code) onSave({ code: v }); else setCode(degree.code); };
  const commitLabel = () => { const v = label.trim(); if (v && v !== degree.label) onSave({ label: v }); else setLabel(degree.label); };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform), transition,
    opacity: isDragging ? 0.6 : 1, ...styles.row,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <button style={styles.handle} {...attributes} {...listeners} aria-label="Déplacer" type="button"><DragDots /></button>
      <input style={{ ...styles.input, width: 90 }} value={code} onChange={(e) => setCode(e.target.value)} onBlur={commitCode}
             onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} title="Code (identifiant stable)" />
      <input style={{ ...styles.input, flex: 1 }} value={label} onChange={(e) => setLabel(e.target.value)} onBlur={commitLabel}
             onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} title="Libellé affiché" />
      <button style={styles.iconBtn} onClick={onDelete} aria-label="Supprimer" title="Supprimer" type="button"><Icon name="trash" size={14} /></button>
    </div>
  );
}

function ChapterRow({ chapter, onSave, onToggleVisible, onDelete }: {
  chapter: Chapter; onSave: (title: string) => void; onToggleVisible: () => void; onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: chapter.id });
  const [title, setTitle] = useState(chapter.title);
  const commit = () => { const v = title.trim(); if (v && v !== chapter.title) onSave(v); else setTitle(chapter.title); };
  const hidden = chapter.visible === false;
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform), transition,
    opacity: isDragging ? 0.6 : hidden ? 0.55 : 1, ...styles.row,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <button style={styles.handle} {...attributes} {...listeners} aria-label="Déplacer" type="button"><DragDots /></button>
      <input style={{ ...styles.input, flex: 1 }} value={title} onChange={(e) => setTitle(e.target.value)} onBlur={commit}
             onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
      <button style={styles.iconBtn} onClick={onToggleVisible} type="button"
              aria-label={hidden ? "Afficher le chapitre" : "Masquer le chapitre"}
              title={hidden ? "Masqué des élèves — cliquer pour afficher" : "Visible — cliquer pour masquer"}>
        <Icon name={hidden ? "eye-off" : "eye"} size={15} />
      </button>
      <button style={styles.iconBtn} onClick={onDelete} aria-label="Supprimer" title="Supprimer" type="button"><Icon name="trash" size={14} /></button>
    </div>
  );
}

function DragDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {[6, 12, 18].flatMap((y) => [8, 16].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  panel: { background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-1)", width: "min(620px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column" },
  head: { display: "flex", alignItems: "center", padding: "16px 18px", borderBottom: "1px solid var(--border-soft)" },
  title: { margin: 0, fontSize: 17, fontWeight: 600, color: "var(--text)", flex: 1 },
  closeBtn: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4, display: "inline-flex" },
  error: { display: "flex", alignItems: "center", gap: 8, margin: "12px 18px 0", padding: "8px 12px", background: "var(--err-box-bg)", color: "var(--red)", borderRadius: "var(--radius)", fontSize: 13 },
  body: { overflowY: "auto", padding: "8px 18px 18px" },
  section: { marginTop: 16 },
  h3: { color: "var(--blue)", fontSize: 14, fontWeight: 600, margin: "0 0 6px" },
  note: { color: "var(--text-muted)", fontSize: 12.5, margin: "0 0 10px", lineHeight: 1.4 },
  row: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  handle: { background: "transparent", border: "none", color: "var(--text-faint)", cursor: "grab", padding: 2, display: "inline-flex", touchAction: "none" },
  // Ligne d'ajout : visuellement distincte de l'édition (action de création).
  addRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, padding: 8, border: "1px dashed var(--border)", borderRadius: "var(--radius)", background: "var(--bg-base)" },
  input: { background: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, padding: "6px 8px", fontFamily: "inherit" },
  iconBtn: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 2, display: "inline-flex", lineHeight: 1 },
};
